import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { setAuth, getToken } from '@/utils/auth';
import BackupCodesView from '@/components/twofactor/BackupCodesView';
import EnrollForm from '@/components/twofactor/EnrollForm';
import ChallengeForm from '@/components/twofactor/ChallengeForm';

const COPY = {
  backup: ['Save your recovery codes', 'Store these somewhere safe — each works once if you lose your phone.'],
  enroll: ['Set up Google Authenticator', 'Scan the QR code with Google Authenticator, then enter the 6-digit code.'],
  challenge: ['Two-factor verification', 'Enter the 6-digit code from your Google Authenticator app.'],
};

const clearMfaSession = () => {
  sessionStorage.removeItem('safer_mfa_token');
  sessionStorage.removeItem('safer_mfa_mode');
};

const popReturnUrl = () => {
  const url = sessionStorage.getItem('safer_return_url');
  sessionStorage.removeItem('safer_return_url');
  return url || '/dashboard';
};

// Routes here with an `mfa_token` + mode ('enroll' | 'challenge'); the session
// is only stored after the code is verified.
export default function TwoFactor() {
  const navigate = useNavigate();
  const mfaToken = sessionStorage.getItem('safer_mfa_token');
  const mode = sessionStorage.getItem('safer_mfa_mode');
  // Already signed in = opted in from Settings, so it can be skipped.
  const canCancel = mode === 'enroll' && !!getToken();

  const [loading, setLoading] = useState(mode === 'enroll');
  const [submitting, setSubmitting] = useState(false);
  const [enroll, setEnroll] = useState(null);
  const [code, setCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [pendingAuth, setPendingAuth] = useState(null);
  const enrollStartedRef = useRef(false);

  const finish = useCallback((data) => {
    clearMfaSession();
    setAuth(data.access_token, data.user);
    navigate(popReturnUrl());
  }, [navigate]);

  useEffect(() => {
    if (!mfaToken || !mode) {
      navigate('/login', { replace: true });
      return;
    }
    if (mode === 'enroll' && !enrollStartedRef.current) {
      enrollStartedRef.current = true; // StrictMode double-mount guard
      api.post('/auth/totp/enroll/start', { mfa_token: mfaToken })
        .then((r) => setEnroll(r.data))
        .catch(() => {
          toast.error('Your session expired. Please sign in again.');
          navigate('/login', { replace: true });
        })
        .finally(() => setLoading(false));
    }
  }, [mfaToken, mode, navigate]);

  const submitCode = async (e, endpoint, onSuccess) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post(endpoint, { mfa_token: mfaToken, code }, { skipErrorToast: true });
      onSuccess(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Incorrect code — try again');
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  const submitEnrollVerify = (e) => submitCode(e, '/auth/totp/enroll/verify', (data) => {
    setBackupCodes(data.backup_codes);
    setPendingAuth(data);
    toast.success('Two-factor authentication enabled');
  });

  const submitChallenge = (e) => submitCode(e, useBackup ? '/auth/totp/backup' : '/auth/totp/verify', (data) => {
    if (useBackup && typeof data.backup_codes_remaining === 'number' && data.backup_codes_remaining <= 2) {
      toast.warning(`Only ${data.backup_codes_remaining} recovery codes left — reset 2FA to get new ones.`);
    }
    finish(data);
  });

  const cancelEnroll = () => {
    clearMfaSession();
    navigate(popReturnUrl());
  };

  const [title, description] = COPY[backupCodes ? 'backup' : mode === 'enroll' ? 'enroll' : 'challenge'];

  const renderBody = () => {
    if (backupCodes) return <BackupCodesView codes={backupCodes} onContinue={() => finish(pendingAuth)} />;
    if (loading) {
      return <div className="flex justify-center py-10"><Loader2 className="h-8 w-8 animate-spin text-indigo-400" /></div>;
    }
    if (mode === 'enroll') {
      return (
        <EnrollForm enroll={enroll} code={code} onCodeChange={setCode} submitting={submitting}
          onSubmit={submitEnrollVerify} onCancel={canCancel ? cancelEnroll : null} />
      );
    }
    return (
      <ChallengeForm code={code} onCodeChange={setCode} useBackup={useBackup} submitting={submitting}
        onToggleBackup={() => { setUseBackup(!useBackup); setCode(''); }} onSubmit={submitChallenge} />
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
      <Card className="w-full max-w-md border-slate-800 bg-slate-900 text-slate-100" data-testid="two-factor-page">
        <CardHeader className="text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-500/15">
            <ShieldCheck className="h-6 w-6 text-indigo-400" />
          </div>
          <CardTitle className="text-xl" data-testid="two-factor-title">{title}</CardTitle>
          <CardDescription className="text-slate-400">{description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">{renderBody()}</CardContent>
      </Card>
    </div>
  );
}
